import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import { AdminSideBar } from "../../../modules/AdminSideBar";
import { Button } from "../../../components/Button";
import { inquiry, service } from "../../../store";

const schema = yup
  .object({
    author_name: yup.string().trim().required("작성자 이름을 입력해주세요"),
    course_id: yup.string().required("강의를 선택해주세요"),
    title: yup.string().trim().required("제목을 입력해주세요"),
    content: yup.string().required("내용을 작성해주세요"),
    created_at: yup.string(),
  })
  .required();

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["blockquote", "link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "align",
  "blockquote",
  "link",
];

// API의 ISO 문자열 <-> datetime-local(input) 변환
const toDatetimeLocal = (iso) => (iso ? String(iso).slice(0, 16) : "");
const fromDatetimeLocal = (v) => (v && v.length === 16 ? `${v}:00` : v || "");

export const AdminReviewEditor = () => {
  const navigate = useNavigate();
  const { review_id } = useParams();
  const isEditMode = Boolean(review_id);

  const {
    getAdminReview,
    createAdminReview,
    updateAdminReview,
    isLoading,
  } = inquiry((state) => ({
    getAdminReview: state.getAdminReview,
    createAdminReview: state.createAdminReview,
    updateAdminReview: state.updateAdminReview,
    isLoading: state.isLoading,
  }));

  const getServices = service((state) => state.getServices);
  const services = service((state) => state.services);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      author_name: "",
      course_id: "",
      title: "",
      content: "",
      created_at: "",
    },
  });

  useEffect(() => {
    getServices();
  }, [getServices]);

  useEffect(() => {
    if (!isEditMode) return;
    (async () => {
      const data = await getAdminReview(review_id);
      if (!data) return;
      reset({
        author_name: data.user_name ?? data.author_name ?? "",
        course_id: data.course_id != null ? String(data.course_id) : "",
        title: data.title ?? "",
        content: data.content ?? "",
        created_at: toDatetimeLocal(data.created_at),
      });
    })();
  }, [isEditMode, review_id, getAdminReview, reset]);

  const onSubmit = async (data) => {
    const payload = {
      author_name: data.author_name.trim(),
      course_id: data.course_id,
      title: data.title.trim(),
      content: data.content,
      created_at: fromDatetimeLocal(data.created_at),
    };

    if (isEditMode) {
      const ok = await updateAdminReview(review_id, payload);
      if (!ok) return;
      alert("고객 후기가 수정되었습니다.");
      navigate("/admin/reviews");
    } else {
      const newId = await createAdminReview(payload);
      if (!newId) return;
      alert("고객 후기가 등록되었습니다.");
      navigate("/admin/reviews");
    }
  };

  return (
    <div className="admin-background">
      <div className="layout-flex">
        <AdminSideBar />
        <main className="admin-main-content">
          <h2 className="admin-page-title">고객 후기 관리</h2>

          <main className="column-editor-page-background">
            <div className="column-editor-page">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="column-editor-title-wrap">
                  <h2 className="column-editor-title">
                    {isEditMode ? "고객 후기 수정" : "고객 후기 작성"}
                  </h2>
                  <Button
                    type="submit"
                    label={isEditMode ? "수정하기" : "저장하기"}
                    disabled={isLoading || isSubmitting}
                    style={{ width: "105px", height: "35px", fontSize: "14px" }}
                  />
                </div>

                <label htmlFor="review-author" className="column-editor-label">
                  작성자 이름
                </label>
                <input
                  {...register("author_name")}
                  id="review-author"
                  type="text"
                  placeholder="화면에 노출될 작성자 이름 (예: 홍길동)"
                  className="column-editor-input"
                />
                {errors.author_name && (
                  <p className="input-error-message">
                    {errors.author_name.message}
                  </p>
                )}

                <label htmlFor="review-course" className="column-editor-label">
                  강의 선택
                </label>
                <select
                  {...register("course_id")}
                  id="review-course"
                  className="column-editor-input"
                >
                  <option value="">강의를 선택해주세요</option>
                  {(services ?? []).map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </select>
                {errors.course_id && (
                  <p className="input-error-message">
                    {errors.course_id.message}
                  </p>
                )}

                <label htmlFor="review-title" className="column-editor-label">
                  제목
                </label>
                <input
                  {...register("title")}
                  id="review-title"
                  type="text"
                  placeholder="후기 제목을 입력해주세요"
                  className="column-editor-input"
                />
                {errors.title && (
                  <p className="input-error-message">{errors.title.message}</p>
                )}

                <label htmlFor="review-date" className="column-editor-label">
                  작성일자 (선택 · 노출 순서 조정용)
                </label>
                <input
                  {...register("created_at")}
                  id="review-date"
                  type="datetime-local"
                  className="column-editor-input"
                />

                <label className="column-editor-label">내용</label>
                <Controller
                  name="content"
                  control={control}
                  render={({ field }) => (
                    <ReactQuill
                      theme="snow"
                      value={field.value}
                      onChange={field.onChange}
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      placeholder="후기 내용을 작성해주세요"
                      className="column-editor-quill"
                    />
                  )}
                />
                {errors.content && (
                  <p className="input-error-message">
                    {errors.content.message}
                  </p>
                )}
              </form>
            </div>
          </main>
        </main>
      </div>
    </div>
  );
};
